import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { UpdateUserAccessRequest, UserAccessResponse } from '@/shared/api';
import { UserAccessEffectivePreview } from './UserAccessEffectivePreview';
import { UserAccessPrivilegeEditor } from './UserAccessPrivilegeEditor';
import { UserAccessScopeEditor } from './UserAccessScopeEditor';
import { useUserAccessForm } from './useUserAccessForm';

export function UserAccessDialog({
  access,
  open,
  onOpenChange,
  userLabel,
  loading,
  onSubmit,
}: {
  access: UserAccessResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userLabel: string;
  loading: boolean;
  onSubmit: (request: UpdateUserAccessRequest) => void;
}) {
  const form = useUserAccessForm(access, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Zugriff bearbeiten</DialogTitle>
          <DialogDescription>{userLabel}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <UserAccessScopeEditor
            role={form.role}
            onRoleChange={form.changeRole}
            scoped={form.scoped}
            companyId={form.companyId}
            onCompanyChange={form.setCompanyId}
            companies={form.companies.data?.items ?? []}
            properties={form.properties.data ?? []}
            propertyIds={form.propertyIds}
            onPropertyToggle={form.toggleProperty}
            loading={loading}
          />
          <UserAccessPrivilegeEditor
            privileges={form.preview.data?.privileges ?? access.privileges}
            overrides={form.overrides}
            onChange={form.setOverride}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={form.resetOverrides}
            disabled={Object.keys(form.overrides).length === 0 || loading}
          >
            Auf Rollenstandard zurücksetzen
          </Button>
          <UserAccessEffectivePreview
            preview={form.preview.data}
            unavailable={form.preview.isError}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Abbrechen
          </Button>
          <Button
            variant="brand"
            loading={loading}
            disabled={!form.changed || form.preview.isLoading || form.preview.isError}
            onClick={() => onSubmit(form.request)}
          >
            Zugriff speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
